import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Loader2,
    Reply,
    Forward,
    Trash2,
    Paperclip,
    Download,
    ChevronLeft,
    Eye,
    EyeOff,
} from "lucide-react";
import {
    getEmailMessage,
    emailAttachmentUrl,
    flagEmailMessage,
    deleteEmailMessage,
} from "../../lib/api";
import { fmtSize } from "./utils";

export default function MessageView({ account, folder, uid, onBack, onDeleted, onCompose }) {
    const [msg, setMsg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const m = await getEmailMessage(account.id, uid, folder, true);
                if (!cancelled) setMsg(m);
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Couldn't load message");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [account?.id, uid, folder]);

    const handleOpenAttachment = async (att) => {
        setBusy(true);
        try {
            const url = await emailAttachmentUrl(account.id, uid, att.index, folder);
            // Open in a new tab so PDFs / images preview inline; other
            // types fall back to the browser's default download handler.
            const w = window.open(url, "_blank", "noopener");
            if (!w) {
                // Popup blocked — fall back to creating an anchor click.
                const a = document.createElement("a");
                a.href = url; a.target = "_blank"; a.rel = "noopener";
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't open attachment");
        } finally {
            setBusy(false);
        }
    };

    const handleDownloadAttachment = async (att) => {
        setBusy(true);
        try {
            const url = await emailAttachmentUrl(account.id, uid, att.index, folder);
            const a = document.createElement("a");
            a.href = url; a.download = att.filename;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 8000);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't download");
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Move this message to Trash?")) return;
        setBusy(true);
        try {
            await deleteEmailMessage(account.id, uid, folder);
            toast.success("Moved to Trash");
            onDeleted();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't delete");
            setBusy(false);
        }
    };

    const handleToggleSeen = async () => {
        if (!msg) return;
        try {
            await flagEmailMessage(account.id, uid, folder, !msg.seen);
            setMsg((m) => ({ ...m, seen: !m.seen }));
        } catch {
            toast.error("Couldn't update flag");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading message…
            </div>
        );
    }
    if (!msg) return null;

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 py-3 border-b border-[#2A3655] flex items-center gap-2 flex-wrap">
                <button
                    onClick={onBack}
                    data-testid="email-message-back"
                    className="md:hidden inline-flex items-center gap-1 text-slate-400 hover:text-white text-xs"
                >
                    <ChevronLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button
                    onClick={() => onCompose("reply", msg)}
                    data-testid="email-message-reply"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#00A3FF] hover:bg-[#3395FF] text-white text-xs font-bold uppercase tracking-wider"
                >
                    <Reply className="w-3.5 h-3.5" /> Reply
                </button>
                <button
                    onClick={() => onCompose("forward", msg)}
                    data-testid="email-message-forward"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] text-slate-200 text-xs font-bold uppercase tracking-wider"
                >
                    <Forward className="w-3.5 h-3.5" /> Forward
                </button>
                <button
                    onClick={handleToggleSeen}
                    data-testid="email-message-toggle-seen"
                    title={msg.seen ? "Mark as unread" : "Mark as read"}
                    className="p-1.5 rounded-sm border border-[#2A3655] hover:border-[#00A3FF] text-slate-300 hover:text-[#00A3FF]"
                >
                    {msg.seen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button
                    onClick={handleDelete}
                    disabled={busy}
                    data-testid="email-message-delete"
                    className="ml-auto p-1.5 rounded-sm border border-red-500/30 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                    title="Delete"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>
            <div className="px-4 py-3 border-b border-[#2A3655]">
                <div className="text-base font-bold text-white mb-1">{msg.subject}</div>
                <div className="text-xs text-slate-400">
                    From: <span className="text-slate-200">{msg.from_name ? `${msg.from_name} ` : ""}&lt;{msg.from_addr}&gt;</span>
                </div>
                <div className="text-xs text-slate-400">To: {msg.to.join(", ")}</div>
                {msg.cc.length > 0 && <div className="text-xs text-slate-400">Cc: {msg.cc.join(", ")}</div>}
                <div className="text-[10px] text-slate-500 mt-1">{msg.date && new Date(msg.date).toLocaleString()}</div>
            </div>
            {msg.attachments.length > 0 && (
                <div className="px-4 py-2 border-b border-[#2A3655] flex flex-wrap gap-2">
                    {msg.attachments.map((a) => (
                        <div
                            key={a.index}
                            className="inline-flex items-stretch text-xs rounded-sm bg-[#1C253C] border border-[#2A3655] overflow-hidden"
                            title={`${a.filename} (${fmtSize(a.size)})`}
                        >
                            <button
                                onClick={() => handleOpenAttachment(a)}
                                data-testid={`email-attachment-${a.index}`}
                                disabled={busy}
                                className="inline-flex items-center gap-1.5 px-2 py-1.5 hover:bg-[#223055] hover:text-[#00A3FF] text-slate-300 disabled:opacity-50"
                            >
                                <Paperclip className="w-3 h-3" />
                                {a.filename}
                            </button>
                            <button
                                onClick={() => handleDownloadAttachment(a)}
                                data-testid={`email-attachment-download-${a.index}`}
                                disabled={busy}
                                title="Download"
                                aria-label={`Download ${a.filename}`}
                                className="px-2 py-1.5 border-l border-[#2A3655] hover:bg-[#223055] hover:text-[#00A3FF] text-slate-400 disabled:opacity-50"
                            >
                                <Download className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex-1 px-4 py-3 overflow-y-auto whitespace-pre-wrap text-sm text-slate-200 leading-relaxed">
                {msg.body_text || (msg.body_html ? <em className="text-slate-500">[HTML-only message — v1 renders plain text only]</em> : <em className="text-slate-500">(empty body)</em>)}
            </div>
        </div>
    );
}
